import { Component, OnInit } from "@angular/core";
import { forkJoin, of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";

import {
  MappedSubRoleView,
  MenuFormModel,
  MenuMasterCreateRequestDTO,
  MenuMasterDeleteRequestDTO,
  MenuMasterResponseDTO,
  MenuMasterUpdateRequestDTO,
  ModuleOption,
  RoleResponseDTO,
  RolesReadAllPaginatedRequestDTO,
  SubRoleCreateRequestDTO,
  SubRoleDeleteRequestDTO,
  SubRoleResponseDTO,
  SubRoleUpdateRequestDTO,
} from "./module-config-interface";

import { ModuleConfigService } from "./module-config.service";

@Component({
  selector: "app-module-config",
  templateUrl: "./module-config.component.html",
  styleUrls: ["./module-config.component.css"],
})
export class ModuleConfigComponent implements OnInit {

  activeTab: "menu" | "mapping" = "menu";

  // ============================================================
  // MODULES
  // ============================================================

  modules: ModuleOption[] = [
    {
      moduleId: 0,
      moduleName: "School",
      moduleLabel: "School",
      key: "school",
    },
    {
      moduleId: 0,
      moduleName: "Hospital",
      moduleLabel: "Hospital",
      key: "hospital",
    },
    {
      moduleId: 0,
      moduleName: "Society",
      moduleLabel: "Housing Society",
      key: "society",
    },
  ];

  // ============================================================
  // DATA
  // ============================================================

  menus: MenuMasterResponseDTO[] = [];
  roles: RoleResponseDTO[] = [];
  roleSubRoles: SubRoleResponseDTO[] = [];

  // ============================================================
  // MENU MASTER
  // ============================================================

  selectedMenuModuleKey: string = "";
  menuSearch: string = "";
  menuForm: MenuFormModel = this.createEmptyMenuForm();
  isSavingMenu: boolean = false;

  // ============================================================
  // ROLE MENU MAPPING
  // ============================================================

  selectedMappingModuleKey: string = "";
  selectedRoleId: number = 0;
  selectedParentMenuId: number = 0;
  selectedSubMenuId: number = 0;

  mappingSearch: string = "";

  isLoadingMappings: boolean = false;
  isSavingMapping: boolean = false;

  // ============================================================
  // COMMON
  // ============================================================

  isLoading: boolean = false;

  constructor(
    private service: ModuleConfigService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  // ============================================================
  // INITIAL LOAD
  // ============================================================

  loadInitialData(): void {

    this.isLoading = true;

    const roleRequest: RolesReadAllPaginatedRequestDTO = {
      pageSize: 1000,
      pageNo: 1,
      whereClause: null,
      orderByClause: "RoleId ASC",
    };

    const menuRequest = this.service
      .readAllMenus()
      .pipe(
        catchError((error) => {

          console.error(
            "MenuMaster ReadAll Error:",
            error,
          );

          this.toastr.error(
            "Unable to load Menu Master list.",
          );

          return of([]);
        }),
      );

    const rolesRequest = this.service
      .readAllRoles(roleRequest)
      .pipe(
        catchError((error) => {

          console.error(
            "Roles ReadAllPaginated Error:",
            error,
          );

          this.toastr.error(
            "Unable to load Role list.",
          );

          return of([]);
        }),
      );

    forkJoin({
      menus: menuRequest,
      roles: rolesRequest,
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (response) => {

          this.menus = this.extractArray(response.menus)
            .map((item: any) => this.normalizeMenu(item))
            .filter((item) => item.isDeleted === 0);

          this.roles = this.extractArray(response.roles)
            .map((item: any) => this.normalizeRole(item))
            .filter((item) => item.isDeleted === 0);

          this.syncModuleIdsFromExistingData();

          this.resetMenuForm();
          this.resetMappingSelection();

          console.log(
            "================ MODULE CONFIG ================",
          );

          console.log(
            "Menus:",
            this.menus,
          );

          console.log(
            "Roles:",
            this.roles,
          );

          console.log(
            "Modules:",
            this.modules,
          );

          console.log(
            "Root Menus:",
            this.menus.filter(
              (x) =>
                Number(
                  x.parentMenuId || 0,
                ) === 0,
            ),
          );
        },
      });
  }

  // ============================================================
  // MODULE ID
  // ============================================================

  private syncModuleIdsFromExistingData(): void {

    this.modules.forEach((module) => {

      const matchingMenu = this.menus.find(
        (menu) =>
          this.normalizeModuleName(
            menu.moduleName,
          ) === module.moduleName &&
          Number(
            menu.moduleId || 0,
          ) > 0,
      );

      if (matchingMenu) {

        module.moduleId =
          Number(
            matchingMenu.moduleId,
          );

        return;
      }

      const matchingRole = this.roles.find(
        (role) =>
          this.getRoleModuleName(
            role,
          ) === module.moduleName &&
          Number(
            role.moduleId || 0,
          ) > 0,
      );

      if (matchingRole) {

        module.moduleId =
          Number(
            matchingRole.moduleId,
          );
      }

    });

  }

  // ============================================================
  // TAB
  // ============================================================

  setTab(
    tab: "menu" | "mapping",
  ): void {

    this.activeTab = tab;
  }

  // ============================================================
  // MODULE NORMALIZATION
  // ============================================================

  private normalizeModuleName(
    value: any,
  ): string {

    const normalized = String(
      value || "",
    )
      .trim()
      .toLowerCase()
      .replace(
        /[\s_-]+/g,
        "",
      );

    if (!normalized) {
      return "";
    }

    // ==========================================================
    // SCHOOL
    // ==========================================================

    if (
      normalized === "school" ||
      normalized === "spe" ||
      normalized === "scl" ||
      normalized === "sclm" ||
      normalized.startsWith("spe") ||
      normalized.startsWith("school") ||
      normalized.includes("school")
    ) {
      return "School";
    }

    // ==========================================================
    // HOSPITAL
    // ==========================================================

    if (
      normalized === "hospital" ||
      normalized === "hm" ||
      normalized === "hospitalmaster" ||
      normalized.startsWith("hospital") ||
      normalized.startsWith("hm")
    ) {
      return "Hospital";
    }

    // ==========================================================
    // SOCIETY
    // ==========================================================

    if (
      normalized === "society" ||
      normalized === "housingsociety" ||
      normalized === "soc" ||
      normalized === "sm" ||
      normalized.startsWith("soc") ||
      normalized.startsWith("society") ||
      normalized.startsWith("sm")
    ) {
      return "Society";
    }

    return "";
  }

  // ============================================================
  // MODULE KEY
  // ============================================================

  getModuleKey(
    moduleId: number,
    moduleName: string,
  ): string {

    const normalized =
      this.normalizeModuleName(
        moduleName,
      );

    if (normalized === "School") {
      return "school";
    }

    if (normalized === "Hospital") {
      return "hospital";
    }

    if (normalized === "Society") {
      return "society";
    }

    return String(
      moduleName || "",
    )
      .trim()
      .toLowerCase();
  }

  // ============================================================
  // SELECTED MODULES
  // ============================================================

  get selectedMenuModule():
    ModuleOption | undefined {

    return this.modules.find(
      (module) =>
        module.key ===
        this.selectedMenuModuleKey,
    );
  }

  get selectedMappingModule():
    ModuleOption | undefined {

    return this.modules.find(
      (module) =>
        module.key ===
        this.selectedMappingModuleKey,
    );
  }

  // ============================================================
  // LEGACY MENU
  // ============================================================

  private isLegacyMenu(
    menu: MenuMasterResponseDTO,
  ): boolean {

    return !this.normalizeModuleName(
      menu.moduleName,
    );
  }

  // ============================================================
  // MENU BELONGS TO MODULE
  // ============================================================

  private menuBelongsToModule(
    menu: MenuMasterResponseDTO,
    moduleName: string,
    allowLegacy: boolean = true,
  ): boolean {

    const normalizedMenuModule =
      this.normalizeModuleName(
        menu.moduleName,
      );

    if (
      normalizedMenuModule ===
      moduleName
    ) {
      return true;
    }

    // Existing Nishwik MenuMaster rows may not
    // contain ModuleName. Keep them available.
    if (
      allowLegacy &&
      !normalizedMenuModule
    ) {
      return true;
    }

    return false;
  }

  // ============================================================
  // ALL MENUS AVAILABLE FOR MODULE
  // ============================================================

  private getMenusForModule(
    moduleName: string,
    activeOnly: boolean = false,
  ): MenuMasterResponseDTO[] {

    let result =
      this.menus.filter(
        (menu) => {

          if (
            menu.isDeleted === 1
          ) {
            return false;
          }

          if (
            activeOnly &&
            menu.isActive !== 1
          ) {
            return false;
          }

          return this.menuBelongsToModule(
            menu,
            moduleName,
            true,
          );
        },
      );

    // If for any old data reason nothing matched,
    // do not leave the dropdown unusable.
    if (
      result.length === 0
    ) {

      result =
        this.menus.filter(
          (menu) => {

            if (
              menu.isDeleted === 1
            ) {
              return false;
            }

            if (
              activeOnly &&
              menu.isActive !== 1
            ) {
              return false;
            }

            return true;
          },
        );
    }

    return result.sort(
      (a, b) =>
        Number(
          a.displayOrder || 0,
        ) -
        Number(
          b.displayOrder || 0,
        ),
    );
  }

  // ============================================================
  // MENU MASTER LIST
  // ============================================================

  get moduleMenus():
    MenuMasterResponseDTO[] {

    const module =
      this.selectedMenuModule;

    if (!module) {
      return [];
    }

    return this.getMenusForModule(
      module.moduleName,
      false,
    );
  }

  // ============================================================
  // SEARCHED MENU MASTER LIST
  // ============================================================

  get filteredModuleMenus():
    MenuMasterResponseDTO[] {

    const search =
      String(
        this.menuSearch || "",
      )
        .trim()
        .toLowerCase();

    if (!search) {
      return this.moduleMenus;
    }

    return this.moduleMenus.filter(
      (menu) => {

        return (
          String(
            menu.menuName || "",
          )
            .toLowerCase()
            .includes(search) ||

          String(
            menu.menuCode || "",
          )
            .toLowerCase()
            .includes(search) ||

          String(
            menu.templatePath || "",
          )
            .toLowerCase()
            .includes(search) ||

          String(
            menu.menuDesc || "",
          )
            .toLowerCase()
            .includes(search)
        );
      },
    );
  }

  // ============================================================
  // ROOT / PARENT MENUS
  // ============================================================

  get parentMenuOptions():
    MenuMasterResponseDTO[] {

    const module =
      this.selectedMenuModule;

    if (!module) {
      return [];
    }

    return this
      .getMenusForModule(
        module.moduleName,
        true,
      )
      .filter(
        (menu) =>
          Number(
            menu.parentMenuId || 0,
          ) === 0 &&
          Number(
            menu.menuId,
          ) !==
            Number(
              this.menuForm.menuId,
            ),
      );
  }

  // ============================================================
  // DEFAULT CHILD MENUS
  // ============================================================

  get defaultChildOptions():
    MenuMasterResponseDTO[] {

    if (
      Number(
        this.menuForm.menuId,
      ) <= 0
    ) {
      return [];
    }

    return this.moduleMenus.filter(
      (menu) =>
        Number(
          menu.parentMenuId || 0,
        ) ===
          Number(
            this.menuForm.menuId,
          ) &&
        Number(
          menu.menuId,
        ) !==
          Number(
            this.menuForm.menuId,
          ) &&
        menu.isDeleted === 0,
    );
  }

  // ============================================================
  // GET MENU NAME
  // ============================================================

  getMenuName(
    menuId: number,
  ): string {

    if (
      !menuId ||
      Number(menuId) === 0
    ) {
      return "Root Menu";
    }

    const menu =
      this.menus.find(
        (item) =>
          Number(
            item.menuId,
          ) ===
          Number(
            menuId,
          ),
      );

    return (
      menu?.menuName ||
      `Menu #${menuId}`
    );
  }

  // ============================================================
  // MENU MODULE CHANGE
  // ============================================================

  onMenuModuleChange(): void {

    this.menuSearch = "";

    this.resetMenuForm();

    console.log(
      "Selected Menu Module:",
      this.selectedMenuModule,
    );

    console.log(
      "Available Parent Menus:",
      this.parentMenuOptions,
    );
  }

  // ============================================================
  // EMPTY FORM
  // ============================================================

  private createEmptyMenuForm():
    MenuFormModel {

    return {
      menuId: 0,
      menuName: "",
      menuCode: "",
      menuDesc: "",
      displayOrder: 1,
      parentMenuId: 0,
      defaultChildMenuId: 0,
      menuIconUrl: "bi bi-grid",
      templatePath: "",
      isActive: 1,
      moduleName: "",
      moduleId: 0,
    };
  }

  // ============================================================
  // RESET MENU FORM
  // ============================================================

  resetMenuForm(): void {

    const module =
      this.selectedMenuModule;

    this.menuForm = {
      ...this.createEmptyMenuForm(),

      moduleName:
        module?.moduleName || "",

      moduleId:
        Number(
          module?.moduleId || 0,
        ),
    };
  }

  // ============================================================
  // EDIT MENU
  // ============================================================

  editMenu(
    menu: MenuMasterResponseDTO,
  ): void {

    let moduleName =
      this.normalizeModuleName(
        menu.moduleName,
      );

    // Legacy menu has blank module.
    // Keep currently selected module.
    if (!moduleName) {

      moduleName =
        this.selectedMenuModule
          ?.moduleName || "";
    }

    const selectedModule =
      this.modules.find(
        (module) =>
          module.moduleName ===
          moduleName,
      );

    if (selectedModule) {

      this.selectedMenuModuleKey =
        selectedModule.key;
    }

    this.menuForm = {

      menuId:
        Number(
          menu.menuId || 0,
        ),

      menuName:
        String(
          menu.menuName || "",
        ),

      menuCode:
        String(
          menu.menuCode || "",
        ),

      menuDesc:
        String(
          menu.menuDesc || "",
        ),

      displayOrder:
        Number(
          menu.displayOrder || 0,
        ),

      parentMenuId:
        Number(
          menu.parentMenuId || 0,
        ),

      defaultChildMenuId:
        Number(
          menu.defaultChildMenuId || 0,
        ),

      menuIconUrl:
        String(
          menu.menuIconUrl || "",
        ),

      templatePath:
        String(
          menu.templatePath || "",
        ),

      isActive:
        Number(
          menu.isActive ?? 1,
        ),

      moduleName:
        moduleName,

      moduleId:
        Number(
          menu.moduleId || 0,
        ),
    };

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  // ============================================================
  // MENU DEFAULTS
  // ============================================================

  fillMenuDefaults(): void {

    const name =
      String(
        this.menuForm.menuName || "",
      ).trim();

    if (!name) {
      return;
    }

    const slug =
      this.slugify(name);

    if (
      !String(
        this.menuForm.menuCode || "",
      ).trim()
    ) {
      this.menuForm.menuCode =
        slug;
    }

    if (
      !String(
        this.menuForm.templatePath || "",
      ).trim()
    ) {
      this.menuForm.templatePath =
        `/${slug}`;
    }
  }

  private slugify(
    value: string,
  ): string {

    return String(
      value || "",
    )
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        "-",
      )
      .replace(
        /^-+|-+$/g,
        "",
      );
  }

  // ============================================================
  // SAVE MENU
  // ============================================================

  saveMenu(): void {

    const module =
      this.selectedMenuModule;

    if (!module) {

      this.toastr.warning(
        "Please select a module.",
      );

      return;
    }

    this.fillMenuDefaults();

    const menuName =
      String(
        this.menuForm.menuName || "",
      ).trim();

    const menuCode =
      String(
        this.menuForm.menuCode || "",
      ).trim();

    if (!menuName) {

      this.toastr.warning(
        "Menu Name is required.",
      );

      return;
    }

    if (!menuCode) {

      this.toastr.warning(
        "Menu Code is required.",
      );

      return;
    }

    if (
      Number(
        this.menuForm.menuId,
      ) > 0 &&
      Number(
        this.menuForm.parentMenuId,
      ) ===
        Number(
          this.menuForm.menuId,
        )
    ) {

      this.toastr.warning(
        "A menu cannot be its own parent.",
      );

      return;
    }

    if (
      Number(
        this.menuForm.menuId,
      ) > 0
    ) {

      this.updateMenu();

      return;
    }

    const duplicate =
      this.menus.some(
        (menu) =>
          String(
            menu.menuCode || "",
          )
            .trim()
            .toLowerCase() ===
            menuCode.toLowerCase() &&
          menu.isDeleted === 0,
      );

    if (duplicate) {

      this.toastr.warning(
        "Menu Code already exists.",
      );

      return;
    }

    const request:
      MenuMasterCreateRequestDTO = {

      menuName:
        menuName,

      menuCode:
        menuCode,

      menuDesc:
        String(
          this.menuForm.menuDesc || "",
        ).trim(),

      displayOrder:
        this.toNumber(
          this.menuForm.displayOrder,
          1,
        ),

      parentMenuId:
        this.toNumber(
          this.menuForm.parentMenuId,
          0,
        ),

      defaultChildMenuId:
        this.toNumber(
          this.menuForm.defaultChildMenuId,
          0,
        ),

      menuIconUrl:
        String(
          this.menuForm.menuIconUrl || "",
        ).trim(),

      templatePath:
        String(
          this.menuForm.templatePath || "",
        ).trim(),

      actionUser:
        this.getActionUser(),

      moduleName:
        module.moduleName,

      moduleId:
        Number(
          module.moduleId || 0,
        ),
    };

    console.log(
      "MenuMaster Create Payload:",
      request,
    );

    this.isSavingMenu = true;

    this.service
      .createMenu(request)
      .pipe(
        finalize(() => {
          this.isSavingMenu = false;
        }),
      )
      .subscribe({
        next: (response) => {

          console.log(
            "MenuMaster Create Response:",
            response,
          );

          this.toastr.success(
            "Menu created successfully.",
          );

          this.resetMenuForm();

          this.reloadMenus();
        },

        error: (error) => {

          console.error(
            "MenuMaster Create Error:",
            error,
          );

          this.showError(
            error,
            "Unable to create menu.",
          );
        },
      });
  }

  // ============================================================
  // UPDATE MENU
  // ============================================================

  private updateMenu(): void {

    const module =
      this.selectedMenuModule;

    if (!module) {

      this.toastr.warning(
        "Please select a module.",
      );

      return;
    }

    const request:
      MenuMasterUpdateRequestDTO = {

      menuId:
        Number(
          this.menuForm.menuId,
        ),

      menuName:
        String(
          this.menuForm.menuName || "",
        ).trim(),

      menuCode:
        String(
          this.menuForm.menuCode || "",
        ).trim(),

      menuDesc:
        String(
          this.menuForm.menuDesc || "",
        ).trim(),

      displayOrder:
        this.toNumber(
          this.menuForm.displayOrder,
          1,
        ),

      parentMenuId:
        this.toNumber(
          this.menuForm.parentMenuId,
          0,
        ),

      defaultChildMenuId:
        this.toNumber(
          this.menuForm.defaultChildMenuId,
          0,
        ),

      menuIconUrl:
        String(
          this.menuForm.menuIconUrl || "",
        ).trim(),

      templatePath:
        String(
          this.menuForm.templatePath || "",
        ).trim(),

      isActive:
        this.toNumber(
          this.menuForm.isActive,
          1,
        ),

      actionUser:
        this.getActionUser(),

      moduleName:
        module.moduleName,

      moduleId:
        Number(
          module.moduleId || 0,
        ),
    };

    console.log(
      "MenuMaster Update Payload:",
      request,
    );

    this.isSavingMenu = true;

    this.service
      .updateMenu(request)
      .pipe(
        finalize(() => {
          this.isSavingMenu = false;
        }),
      )
      .subscribe({
        next: (response) => {

          console.log(
            "MenuMaster Update Response:",
            response,
          );

          this.toastr.success(
            "Menu updated successfully.",
          );

          this.resetMenuForm();

          this.reloadMenus();
        },

        error: (error) => {

          console.error(
            "MenuMaster Update Error:",
            error,
          );

          this.showError(
            error,
            "Unable to update menu.",
          );
        },
      });
  }

  // ============================================================
  // DELETE MENU
  // ============================================================

  deleteMenu(
    menu: MenuMasterResponseDTO,
  ): void {

    const children =
      this.menus.filter(
        (item) =>
          Number(
            item.parentMenuId || 0,
          ) ===
            Number(
              menu.menuId,
            ) &&
          item.isDeleted === 0,
      );

    if (
      children.length > 0
    ) {

      this.toastr.warning(
        "This menu has sub menus. Delete or move its sub menus first.",
      );

      return;
    }

    const confirmed =
      window.confirm(
        `Delete "${menu.menuName}"?`,
      );

    if (!confirmed) {
      return;
    }

    const request:
      MenuMasterDeleteRequestDTO = {

      menuId:
        Number(
          menu.menuId,
        ),

      actionUser:
        this.getActionUser(),
    };

    this.service
      .deleteMenu(request)
      .subscribe({
        next: () => {

          this.toastr.success(
            "Menu deleted successfully.",
          );

          if (
            this.menuForm.menuId ===
            menu.menuId
          ) {

            this.resetMenuForm();
          }

          this.reloadMenus();
        },

        error: (error) => {

          this.showError(
            error,
            "Unable to delete menu.",
          );
        },
      });
  }

  // ============================================================
  // RELOAD MENUS
  // ============================================================

  reloadMenus(): void {

    this.service
      .readAllMenus()
      .subscribe({
        next: (response) => {

          this.menus =
            this.extractArray(
              response,
            )
              .map(
                (item: any) =>
                  this.normalizeMenu(
                    item,
                  ),
              )
              .filter(
                (item) =>
                  item.isDeleted === 0,
              );

          this.syncModuleIdsFromExistingData();

          if (
            this.selectedRoleId > 0
          ) {

            this.loadRoleMappings();
          }
        },

        error: (error) => {

          console.error(
            "MenuMaster Refresh Error:",
            error,
          );

          this.showError(
            error,
            "Unable to refresh menu list.",
          );
        },
      });
  }

  // ============================================================
  // MAPPING MODULE CHANGE
  // ============================================================

  onMappingModuleChange(): void {

    this.mappingSearch = "";

    this.resetMappingSelection();

    console.log(
      "Selected Mapping Module:",
      this.selectedMappingModule,
    );
  }

  // ============================================================
  // RESET MAPPING
  // ============================================================

  resetMappingSelection(): void {

    this.selectedRoleId = 0;
    this.selectedParentMenuId = 0;
    this.selectedSubMenuId = 0;

    this.roleSubRoles = [];
  }

  // ============================================================
  // ROLE MODULE
  // ============================================================

  private getRoleModuleName(
    role: RoleResponseDTO,
  ): string {

    let moduleName =
      this.normalizeModuleName(
        role.moduleName,
      );

    if (moduleName) {
      return moduleName;
    }

    moduleName =
      this.normalizeModuleName(
        role.groupName,
      );

    if (moduleName) {
      return moduleName;
    }

    moduleName =
      this.normalizeModuleName(
        role.roleCode,
      );

    if (moduleName) {
      return moduleName;
    }

    moduleName =
      this.normalizeModuleName(
        role.roleName,
      );

    return moduleName;
  }

  // ============================================================
  // ROLES FOR MODULE
  // ============================================================

  get mappingRoles():
    RoleResponseDTO[] {

    const module =
      this.selectedMappingModule;

    if (!module) {
      return [];
    }

    const activeRoles =
      this.roles.filter(
        (role) =>
          role.isActive === 1 &&
          role.isDeleted === 0,
      );

    const matchingRoles =
      activeRoles.filter(
        (role) =>
          this.getRoleModuleName(
            role,
          ) ===
          module.moduleName,
      );

    // Keep old behaviour safe:
    // if module tagging is unavailable,
    // don't make Role dropdown empty.
    const finalRoles =
      matchingRoles.length > 0
        ? matchingRoles
        : activeRoles;

    return finalRoles.sort(
      (a, b) =>
        String(
          a.roleName || "",
        ).localeCompare(
          String(
            b.roleName || "",
          ),
        ),
    );
  }

  // ============================================================
  // MENUS AVAILABLE FOR ROLE MAPPING
  // ============================================================

  get mappingModuleMenus():
    MenuMasterResponseDTO[] {

    const module =
      this.selectedMappingModule;

    if (!module) {
      return [];
    }

    return this.getMenusForModule(
      module.moduleName,
      true,
    );
  }

  // ============================================================
  // ROOT / PARENT MENUS FOR MAPPING
  // ============================================================

  get mappingParentMenus():
    MenuMasterResponseDTO[] {

    return this.mappingModuleMenus.filter(
      (menu) =>
        Number(
          menu.parentMenuId || 0,
        ) === 0,
    );
  }

  // ============================================================
  // SUB MENUS FOR SELECTED PARENT
  // ============================================================

  get mappingSubMenus():
    MenuMasterResponseDTO[] {

    if (
      !this.selectedParentMenuId
    ) {
      return [];
    }

    return this.mappingModuleMenus.filter(
      (menu) =>
        Number(
          menu.parentMenuId || 0,
        ) ===
          Number(
            this.selectedParentMenuId,
          ),
    );
  }

  // ============================================================
  // PARENT MENU CHANGE
  // ============================================================

  onParentMenuChange(): void {

    this.selectedSubMenuId = 0;

    console.log(
      "Selected Parent Menu:",
      this.selectedParentMenuId,
    );

    console.log(
      "Sub Menu Options:",
      this.mappingSubMenus,
    );
  }

  // ============================================================
  // ROLE CHANGE
  // ============================================================

  onRoleChange(): void {

    this.selectedParentMenuId = 0;
    this.selectedSubMenuId = 0;

    this.loadRoleMappings();

    console.log(
      "Selected Role:",
      this.selectedRoleId,
    );

    console.log(
      "Parent Menu Options:",
      this.mappingParentMenus,
    );
  }

  // ============================================================
  // LOAD ROLE MAPPING
  // ============================================================

  loadRoleMappings(): void {

    if (
      !this.selectedRoleId
    ) {

      this.roleSubRoles = [];

      return;
    }

    this.isLoadingMappings = true;

    this.service
      .readSubRolesByRoleId({
        roleId:
          Number(
            this.selectedRoleId,
          ),
      })
      .pipe(
        finalize(() => {

          this.isLoadingMappings = false;
        }),
      )
      .subscribe({
        next: (response) => {

          this.roleSubRoles =
            this.extractArray(
              response,
            )
              .map(
                (item: any) =>
                  this.normalizeSubRole(
                    item,
                  ),
              );

          console.log(
            "Role SubRoles:",
            this.roleSubRoles,
          );

          console.log(
            "Mapping Root Menus:",
            this.mappingParentMenus,
          );
        },

        error: (error) => {

          console.error(
            "SubRoles ReadByRoleId Error:",
            error,
          );

          this.roleSubRoles = [];

          this.showError(
            error,
            "Unable to load role menu mappings.",
          );
        },
      });
  }

  // ============================================================
  // MAPPED ROLE MENUS
  // ============================================================

  get mappedRoleMenus():
    MappedSubRoleView[] {

    const module =
      this.selectedMappingModule;

    if (!module) {
      return [];
    }

    const search =
      String(
        this.mappingSearch || "",
      )
        .trim()
        .toLowerCase();

    const result:
      MappedSubRoleView[] = [];

    this.roleSubRoles
      .filter(
        (subRole) =>
          subRole.isDeleted === 0,
      )
      .forEach(
        (subRole) => {

          const menu =
            this.menus.find(
              (item) =>
                Number(
                  item.menuId,
                ) ===
                Number(
                  subRole.menuId,
                ),
            );

          if (!menu) {
            return;
          }

          const menuModule =
            this.normalizeModuleName(
              menu.moduleName,
            );

          // If menu has an explicit module and it
          // belongs to another module, exclude it.
          //
          // If ModuleName is blank, keep it because
          // its SubRole mapping proves the role uses it.
          if (
            menuModule &&
            menuModule !==
              module.moduleName
          ) {
            return;
          }

          const parent =
            Number(
              menu.parentMenuId || 0,
            ) > 0
              ? this.menus.find(
                  (item) =>
                    Number(
                      item.menuId,
                    ) ===
                    Number(
                      menu.parentMenuId,
                    ),
                )
              : undefined;

          const row:
            MappedSubRoleView = {

            subRoleId:
              subRole.subRoleId,

            roleId:
              subRole.roleId,

            menuId:
              menu.menuId,

            menuName:
              menu.menuName,

            menuCode:
              menu.menuCode,

            parentMenuId:
              Number(
                menu.parentMenuId || 0,
              ),

            parentMenuName:
              parent?.menuName ||
              "Root",

            templatePath:
              menu.templatePath,

            displayOrder:
              Number(
                menu.displayOrder || 0,
              ),

            isActive:
              subRole.isActive,

            isDeleted:
              subRole.isDeleted,

            isDefault:
              subRole.isDefault,
          };

          if (search) {

            const matches =
              String(
                row.menuName || "",
              )
                .toLowerCase()
                .includes(search) ||

              String(
                row.menuCode || "",
              )
                .toLowerCase()
                .includes(search) ||

              String(
                row.parentMenuName || "",
              )
                .toLowerCase()
                .includes(search) ||

              String(
                row.templatePath || "",
              )
                .toLowerCase()
                .includes(search);

            if (!matches) {
              return;
            }
          }

          result.push(row);
        },
      );

    return result.sort(
      (a, b) =>
        Number(
          a.displayOrder || 0,
        ) -
        Number(
          b.displayOrder || 0,
        ),
    );
  }

  // ============================================================
  // ALREADY MAPPED
  // ============================================================

  isMenuAlreadyMapped(
    menuId: number,
  ): boolean {

    return this.roleSubRoles.some(
      (item) =>
        Number(
          item.menuId,
        ) ===
          Number(
            menuId,
          ) &&
        item.isDeleted === 0 &&
        item.isActive === 1,
    );
  }

  // ============================================================
  // ASSIGN MENU
  // ============================================================

  assignMenuToRole(): void {

    if (
      !this.selectedMappingModule
    ) {

      this.toastr.warning(
        "Please select a module.",
      );

      return;
    }

    if (
      !this.selectedRoleId
    ) {

      this.toastr.warning(
        "Please select a role.",
      );

      return;
    }

    if (
      !this.selectedParentMenuId
    ) {

      this.toastr.warning(
        "Please select a parent menu.",
      );

      return;
    }

    const menuIds =
      new Set<number>();

    menuIds.add(
      Number(
        this.selectedParentMenuId,
      ),
    );

    if (
      Number(
        this.selectedSubMenuId,
      ) > 0
    ) {

      menuIds.add(
        Number(
          this.selectedSubMenuId,
        ),
      );
    }

    const requests: any[] = [];

    Array.from(
      menuIds,
    ).forEach(
      (menuId) => {

        const existing =
          this.roleSubRoles.find(
            (item) =>
              Number(
                item.menuId,
              ) ===
                Number(
                  menuId,
                ) &&
              item.isDeleted === 0,
          );

        // Already active.
        if (
          existing &&
          existing.isActive === 1
        ) {
          return;
        }

        // Existing but disabled.
        if (
          existing &&
          existing.isActive !== 1
        ) {

          const updateRequest:
            SubRoleUpdateRequestDTO = {

            subRoleId:
              existing.subRoleId,

            isActive: 1,

            isDeleted: 0,

            actionUser:
              this.getActionUser(),
          };

          requests.push(
            this.service.updateSubRole(
              updateRequest,
            ),
          );

          return;
        }

        // New mapping.
        const createRequest:
          SubRoleCreateRequestDTO = {

          roleId:
            Number(
              this.selectedRoleId,
            ),

          menuId:
            Number(
              menuId,
            ),

          actionUser:
            this.getActionUser(),
        };

        requests.push(
          this.service.createSubRole(
            createRequest,
          ),
        );
      },
    );

    if (
      requests.length === 0
    ) {

      this.toastr.info(
        "Selected menu is already assigned to this role.",
      );

      return;
    }

    console.log(
      "Assign Menu IDs:",
      Array.from(menuIds),
    );

    this.isSavingMapping = true;

    forkJoin(
      requests,
    )
      .pipe(
        finalize(() => {
          this.isSavingMapping = false;
        }),
      )
      .subscribe({
        next: () => {

          this.toastr.success(
            "Menu assigned to role successfully.",
          );

          this.selectedParentMenuId = 0;
          this.selectedSubMenuId = 0;

          this.loadRoleMappings();
        },

        error: (error) => {

          console.error(
            "SubRole Create Error:",
            error,
          );

          this.showError(
            error,
            "Unable to assign menu to role.",
          );
        },
      });
  }

  // ============================================================
  // DISABLE
  // ============================================================

  disableRoleMenu(
    row: MappedSubRoleView,
  ): void {

    const confirmed =
      window.confirm(
        `Disable "${row.menuName}" for this role?`,
      );

    if (!confirmed) {
      return;
    }

    const request:
      SubRoleUpdateRequestDTO = {

      subRoleId:
        row.subRoleId,

      isActive: 0,

      isDeleted: 0,

      actionUser:
        this.getActionUser(),
    };

    this.service
      .updateSubRole(request)
      .subscribe({
        next: () => {

          this.toastr.success(
            "Role menu disabled successfully.",
          );

          this.loadRoleMappings();
        },

        error: (error) => {

          this.showError(
            error,
            "Unable to update role menu.",
          );
        },
      });
  }

  // ============================================================
  // REMOVE MAPPING
  // ============================================================

  removeRoleMenu(
    row: MappedSubRoleView,
  ): void {

    const confirmed =
      window.confirm(
        `Remove "${row.menuName}" from this role?`,
      );

    if (!confirmed) {
      return;
    }

    const request:
      SubRoleDeleteRequestDTO = {

      subRoleId:
        row.subRoleId,

      actionUser:
        this.getActionUser(),
    };

    this.service
      .deleteSubRole(request)
      .subscribe({
        next: () => {

          this.toastr.success(
            "Role menu mapping removed successfully.",
          );

          this.loadRoleMappings();
        },

        error: (error) => {

          this.showError(
            error,
            "Unable to remove role menu mapping.",
          );
        },
      });
  }

  // ============================================================
  // EXTRACT RESPONSE ARRAY
  // ============================================================

  private extractArray(
    response: any,
  ): any[] {

    if (!response) {
      return [];
    }

    if (
      Array.isArray(response)
    ) {
      return response;
    }

    const candidates = [
      response?.items,
      response?.Items,

      response?.data,
      response?.Data,

      response?.data?.items,
      response?.Data?.Items,

      response?.result,
      response?.Result,

      response?.result?.items,
      response?.Result?.Items,

      response?.responseData,
      response?.ResponseData,

      response?.responseData?.items,
      response?.ResponseData?.Items,

      response?.value,
      response?.Value,

      response?.list,
      response?.List,
    ];

    for (
      const candidate of candidates
    ) {

      if (
        Array.isArray(candidate)
      ) {
        return candidate;
      }
    }

    if (
      response?.item
    ) {
      return [
        response.item,
      ];
    }

    if (
      response?.Item
    ) {
      return [
        response.Item,
      ];
    }

    return [];
  }

  // ============================================================
  // NORMALIZE MENU
  // ============================================================

  private normalizeMenu(
    item: any,
  ): MenuMasterResponseDTO {

    return {

      menuId:
        this.toNumber(
          item?.menuId ??
          item?.MenuId,
          0,
        ),

      menuName:
        String(
          item?.menuName ??
          item?.MenuName ??
          "",
        ),

      menuCode:
        String(
          item?.menuCode ??
          item?.MenuCode ??
          "",
        ),

      menuDesc:
        String(
          item?.menuDesc ??
          item?.MenuDesc ??
          "",
        ),

      displayOrder:
        this.toNumber(
          item?.displayOrder ??
          item?.DisplayOrder,
          0,
        ),

      parentMenuId:
        this.toNumber(
          item?.parentMenuId ??
          item?.ParentMenuId,
          0,
        ),

      defaultChildMenuId:
        this.toNumber(
          item?.defaultChildMenuId ??
          item?.DefaultChildMenuId,
          0,
        ),

      menuIconUrl:
        String(
          item?.menuIconUrl ??
          item?.MenuIconUrl ??
          "",
        ),

      templatePath:
        String(
          item?.templatePath ??
          item?.TemplatePath ??
          "",
        ),

      isActive:
        this.toNumber(
          item?.isActive ??
          item?.IsActive,
          1,
        ),

      isDeleted:
        this.toNumber(
          item?.isDeleted ??
          item?.IsDeleted,
          0,
        ),

      createdBy:
        item?.createdBy ??
        item?.CreatedBy,

      createdOn:
        item?.createdOn ??
        item?.CreatedOn,

      modifiedBy:
        item?.modifiedBy ??
        item?.ModifiedBy,

      modifiedOn:
        item?.modifiedOn ??
        item?.ModifiedOn,

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          "",
        ),

      moduleId:
        this.toNumber(
          item?.moduleId ??
          item?.ModuleId,
          0,
        ),
    };
  }

  // ============================================================
  // NORMALIZE ROLE
  // ============================================================

  private normalizeRole(
    item: any,
  ): RoleResponseDTO {

    return {

      roleId:
        this.toNumber(
          item?.roleId ??
          item?.RoleId,
          0,
        ),

      roleName:
        String(
          item?.roleName ??
          item?.RoleName ??
          "",
        ),

      roleCode:
        String(
          item?.roleCode ??
          item?.RoleCode ??
          "",
        ),

      roleDesc:
        String(
          item?.roleDesc ??
          item?.RoleDesc ??
          "",
        ),

      groupName:
        String(
          item?.groupName ??
          item?.GroupName ??
          "",
        ),

      isActive:
        this.toNumber(
          item?.isActive ??
          item?.IsActive,
          1,
        ),

      isDeleted:
        this.toNumber(
          item?.isDeleted ??
          item?.IsDeleted,
          0,
        ),

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          "",
        ),

      moduleId:
        this.toNumber(
          item?.moduleId ??
          item?.ModuleId,
          0,
        ),

      createdBy:
        item?.createdBy ??
        item?.CreatedBy,

      createdOn:
        item?.createdOn ??
        item?.CreatedOn,

      modifiedBy:
        item?.modifiedBy ??
        item?.ModifiedBy,

      modifiedOn:
        item?.modifiedOn ??
        item?.ModifiedOn,
    };
  }

  // ============================================================
  // NORMALIZE SUBROLE
  // ============================================================

  private normalizeSubRole(
    item: any,
  ): SubRoleResponseDTO {

    return {

      subRoleId:
        this.toNumber(
          item?.subRoleId ??
          item?.SubRoleId,
          0,
        ),

      roleId:
        this.toNumber(
          item?.roleId ??
          item?.RoleId,
          0,
        ),

      subRoleName:
        String(
          item?.subRoleName ??
          item?.SubRoleName ??
          "",
        ),

      subRoleCode:
        String(
          item?.subRoleCode ??
          item?.SubRoleCode ??
          "",
        ),

      subRoleDesc:
        String(
          item?.subRoleDesc ??
          item?.SubRoleDesc ??
          "",
        ),

      menuId:
        this.toNumber(
          item?.menuId ??
          item?.MenuId,
          0,
        ),

      displayOrder:
        this.toNumber(
          item?.displayOrder ??
          item?.DisplayOrder,
          0,
        ),

      isDefault:
        this.toNumber(
          item?.isDefault ??
          item?.IsDefault,
          0,
        ),

      isActive:
        this.toNumber(
          item?.isActive ??
          item?.IsActive,
          1,
        ),

      isDeleted:
        this.toNumber(
          item?.isDeleted ??
          item?.IsDeleted,
          0,
        ),

      createdBy:
        item?.createdBy ??
        item?.CreatedBy,

      createdOn:
        item?.createdOn ??
        item?.CreatedOn,

      modifiedBy:
        item?.modifiedBy ??
        item?.ModifiedBy,

      modifiedOn:
        item?.modifiedOn ??
        item?.ModifiedOn,
    };
  }

  // ============================================================
  // NUMBER
  // ============================================================

  private toNumber(
    value: any,
    fallback: number,
  ): number {

    if (
      value === true
    ) {
      return 1;
    }

    if (
      value === false
    ) {
      return 0;
    }

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return fallback;
    }

    const parsed =
      Number(value);

    return Number.isFinite(
      parsed,
    )
      ? parsed
      : fallback;
  }

  // ============================================================
  // ACTION USER
  // ============================================================

  private getActionUser():
    string {

    const directKeys = [
      "userId",
      "UserId",
      "UserID",
      "actionUser",
      "ActionUser",
    ];

    for (
      const key of directKeys
    ) {

      const value =
        localStorage.getItem(
          key,
        );

      if (
        value &&
        value !== "null" &&
        value !== "undefined"
      ) {

        return value
          .replace(
            /^"|"$/g,
            "",
          )
          .trim();
      }
    }

    const objectKeys = [
      "user",
      "User",
      "userData",
      "loginUser",
    ];

    for (
      const key of objectKeys
    ) {

      const value =
        localStorage.getItem(
          key,
        );

      if (!value) {
        continue;
      }

      try {

        const parsed =
          JSON.parse(value);

        const userId =
          parsed?.userId ??
          parsed?.UserId ??
          parsed?.id ??
          parsed?.Id;

        if (
          userId !== null &&
          userId !== undefined
        ) {

          return String(
            userId,
          );
        }

      } catch {}
    }

    return "system";
  }

  // ============================================================
  // ERROR
  // ============================================================

  private showError(
    error: any,
    fallback: string,
  ): void {

    const message =
      error?.error?.message ??
      error?.error?.Message ??
      error?.error?.title ??
      error?.error?.Title ??
      error?.message ??
      fallback;

    this.toastr.error(
      String(message),
    );
  }
}